/**
 * API Service
 * Centralized API configuration and CESVI endpoint integration
 */

import axios from 'axios';

const API_CONFIG = {
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://qa-api.oc.coal.mx',
  endpoints: {
    cesviSearch: '/api/audit-search/serviceCESVIFull'
  },
  timeout: 30000 // 30 seconds
};

// Create axios instance
const apiClient = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Fetch VIN data from CESVI API
 * @param {string} vin - Vehicle Identification Number (17 characters)
 * @returns {Promise<Object>} - CESVI response with vehicle versions
 */
export const fetchVinData = async (vin) => {
  try {
    const response = await apiClient.get(API_CONFIG.endpoints.cesviSearch, {
      params: {
        serialNumber: vin
      }
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Handle API errors with user-friendly messages
 * @param {Error} error - Axios error object
 * @returns {Error} - Formatted error with message and status
 */
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;

    switch (status) {
      case 400:
        return new Error(data?.error?.message || 'Parámetros de búsqueda inválidos');
      case 404:
        return new Error('No se encontraron datos para este VIN');
      case 429:
        return new Error('Límite de consultas excedido. Intente más tarde');
      case 500:
        return new Error(data?.error?.message || 'Error del servidor CESVI');
      default:
        return new Error(`Error del servidor: ${status}`);
    }
  }

  if (error.code === 'ECONNABORTED') {
    return new Error('Tiempo de espera agotado. Verifique su conexión');
  }

  if (error.request) {
    return new Error('No se pudo conectar con el servidor. Verifique su conexión a internet');
  }

  return new Error(error.message || 'Error desconocido');
};

/**
 * Validate VIN format
 * @param {string} vin - VIN to validate
 * @returns {Object} - { isValid: boolean, error?: string }
 */
export const validateVin = (vin) => {
  if (!vin) {
    return { isValid: false, error: 'El número VIN es requerido' };
  }

  const cleanVin = vin.replace(/\s/g, '').toUpperCase();

  if (cleanVin.length !== 17) {
    return { isValid: false, error: `El VIN debe tener 17 caracteres (actual: ${cleanVin.length})` };
  }

  if (/[IOQ]/.test(cleanVin)) {
    return { isValid: false, error: 'El VIN no puede contener las letras I, O o Q' };
  }

  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(cleanVin)) {
    return { isValid: false, error: 'El VIN contiene caracteres inválidos' };
  }

  return { isValid: true, cleanVin };
};

export default {
  fetchVinData,
  validateVin
};
